import "./SksdLog.css";
import * as React from "react";
import { AppConfig } from "../../AppConfig";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import Graphic from "@arcgis/core/Graphic";
import Loader from "../genericComponents/Loader/Loader";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import NfsDropdown from "../genericComponents/NfsDropdown/NfsDropdown";
import { useCallback, useRef } from "react";
import _ from "lodash";
import { SksdStatus } from "../../types/types";
import Modal from "react-modal";
import esriConfig from "@arcgis/core/config";
import InputField from "../genericComponents/InputField/InputField";
import { UserContext } from "../../Context";
import { hasAccessToGroups } from "../../utils/helpers";
import SksdLogItem from "./SksdLogItem";

export function SksdLog() {
    const [isLoading, setIsLoading] = React.useState<boolean>(true);
    const [logData, setLogData] = React.useState<Graphic[]>([]);
    const [logLayer, setLogLayer] = React.useState<FeatureLayer | null>(null);
    const [xmlContent, setXmlContent] = React.useState<string | null>(null);
    const [xmlShowHeader, setXmlShowHeader] = React.useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const [currentPage, setCurrentPage] = React.useState<number>(1);
    const [pageSize, setPageSize] = React.useState<number>(20);
    const [totalCount, setTotalCount] = React.useState<number>(0);
    const [showSearchPanel, setShowSearchPanel] = React.useState<boolean>(false);
    const [searchTermInput, setSearchTermInput] = React.useState<string>("");
    const [statusNameInput, setStatusNameInput] = React.useState<string | null>(null);
    const [activeSearchTerm, setActiveSearchTerm] = React.useState<string>("");
    const [activeStatusName, setActiveStatusName] = React.useState<string | null>(null);
    const [fromDateInput, setFromDateInput] = React.useState<string>("");
    const [toDateInput, setToDateInput] = React.useState<string>("");
    const [activeFromDate, setActiveFromDate] = React.useState<string>("");
    const [activeToDate, setActiveToDate] = React.useState<string>("");

    const userContext = React.useContext(UserContext);
    const userSelf = userContext?.userSelf.value;
    const isSksdAdmin = hasAccessToGroups(userSelf, [AppConfig.GroupAccess.SksdAdmin]);

    // Debounce applying filters
    const debounceApplyFiltersRef = useRef<ReturnType<typeof _.debounce> | null>(null);
    React.useEffect(() => {
        if (debounceApplyFiltersRef.current) {
            debounceApplyFiltersRef.current.cancel();
        }
        debounceApplyFiltersRef.current = _.debounce(() => {
            setActiveSearchTerm(searchTermInput.trim());
            setActiveStatusName(statusNameInput);
            // Validate date range (basic): if from > to, ignore to
            const fromDate = fromDateInput;
            let toDate = toDateInput;
            if (fromDate && toDate && fromDate > toDate) {
                toDate = ""; // simple handling: drop invalid upper bound
            }
            setActiveFromDate(fromDate);
            setActiveToDate(toDate);
            setCurrentPage(1); // reset pagination when filters change
        }, 300);
        debounceApplyFiltersRef.current();
    }, [searchTermInput, statusNameInput, fromDateInput, toDateInput]);

    const forceJsonFormatInQuery = (featureLayerUrl: string) => {
        // the 'melding' attribute is of type esriFieldTypeXML and it does not get returned when querying with f=pbf
        esriConfig.request.interceptors?.push({
            urls: featureLayerUrl,
            before: function (params) {
                const query = params.requestOptions.query as Record<string, unknown> | null | undefined;
                if (query?.f === "pbf") {
                    query.f = "json";
                }
            },
        });
    };

    const fetchLogData = useCallback(async () => {
        if (!logLayer) {
            return;
        }
        try {
            setIsLoading(true);
            const query = logLayer.createQuery();
            const whereClauses: string[] = [];
            if (activeSearchTerm) {
                // escape single quotes
                const esc = activeSearchTerm.replace(/'/g, "''");
                whereClauses.push(`(navn LIKE '%${esc}%' OR epost LIKE '%${esc}%' OR bruker LIKE '%${esc}%' )`);
            }
            if (activeStatusName) {
                const statusValue = (SksdStatus as unknown as Record<string, number>)[activeStatusName];
                if (Number.isFinite(statusValue)) {
                    whereClauses.push(`status = ${statusValue}`);
                }
            }
            if (activeFromDate) {
                whereClauses.push(`dato >= DATE '${activeFromDate}'`);
            }
            if (activeToDate) {
                whereClauses.push(`dato <= DATE '${activeToDate}'`);
            }
            query.where = whereClauses.length ? whereClauses.join(" AND ") : "1=1";
            query.outFields = ["*"];
            query.returnGeometry = false;
            query.orderByFields = ["dato DESC"];

            // First, get total count (use FeatureLayer.queryFeatureCount for efficiency)
            const count = await logLayer.queryFeatureCount(query);
            setTotalCount(count);

            // Then, fetch current page with resultOffset/resultRecordCount
            const offset = (currentPage - 1) * pageSize;
            query.start = offset; // alias for resultOffset
            query.num = pageSize; // alias for resultRecordCount

            const result = await logLayer.queryFeatures(query);
            setLogData(result.features);
            setIsLoading(false);
        } catch (error) {
            console.error("Error fetching log data:", error);
            setIsLoading(false);
        }
    }, [logLayer, currentPage, pageSize, activeSearchTerm, activeStatusName, activeFromDate, activeToDate]);

    const reloadLogs = () => {
        fetchLogData();
    };

    React.useEffect(() => {
        fetchLogData();
    }, [logLayer, fetchLogData]);

    React.useEffect(() => {
        if (!logLayer) {
            const layer = new FeatureLayer({
                url: AppConfig.ArcGIS.BaseUrl + AppConfig.ArcGIS.SksdFeatureServer + "/0",
                outFields: ["*"],
            });
            forceJsonFormatInQuery(layer.url as string);
            setLogLayer(layer);
        }
    }, [logLayer]);

    const showSksdXml = (log: Graphic, response: boolean) => {
        if (response) {
            setXmlContent(log.attributes.melding_respons);
            setXmlShowHeader("SKSD XML Melding - Respons for " + log.attributes.navn);
        } else {
            setXmlContent(log.attributes.melding);
            setXmlShowHeader("SKSD XML Melding for " + log.attributes.navn);
        }
        setIsModalOpen(true);
    };

    return (
        <div className="sksd-log-container">
            {isLoading && <Loader overlay="element" targetElement=".sksd-log-container" size={40} />}

            <div className="sksd-log-header">
                <strong>SKSD Logg</strong>
                <NfsButton small onClick={reloadLogs} className="sksd-log-container-reload-button">
                    Hent på nytt
                </NfsButton>
            </div>

            <div className="sksd-log-toolbar">
                <div className="sksd-log-search-toggle">
                    <NfsButton
                        small
                        outlined
                        onClick={() => setShowSearchPanel((p) => !p)}
                        className="sksd-log-pagination-button"
                    >
                        {showSearchPanel ? "Skjul filter" : "Vis filter"}
                    </NfsButton>
                </div>
                {showSearchPanel && (
                    <div className="sksd-log-search-panel">
                        <div className="sksd-log-search-field">
                            <span>Søk</span>
                            <InputField
                                inputFieldId="sksd-log-search-input"
                                label=""
                                type="text"
                                value={searchTermInput}
                                placeholder="Sjømerkenr, bruker, epost, pakkenavn"
                                onChange={(e) => setSearchTermInput(e)}
                            />
                        </div>
                        <div className="sksd-log-search-field">
                            <span>Status</span>
                            {(() => {
                                // Collect enum names (filter out numeric reverse mappings)
                                const statusNames = Object.keys(SksdStatus).filter((k) => Number.isNaN(Number(k)));
                                return (
                                    <NfsDropdown
                                        options={statusNames}
                                        selectedOption={statusNameInput}
                                        setSelectedOption={(value: string | null) => {
                                            setStatusNameInput(value);
                                        }}
                                        allowEmpty
                                        placeholder="Alle"
                                    />
                                );
                            })()}
                        </div>
                        <div className="sksd-log-search-field">
                            <span>Fra dato</span>
                            <InputField
                                inputFieldId="sksd-log-from-date"
                                label=""
                                type="date"
                                value={fromDateInput}
                                onChange={(v) => setFromDateInput(v)}
                            />
                        </div>
                        <div className="sksd-log-search-field">
                            <span>Til dato</span>
                            <InputField
                                inputFieldId="sksd-log-to-date"
                                label=""
                                type="date"
                                value={toDateInput}
                                onChange={(v) => setToDateInput(v)}
                            />
                        </div>
                    </div>
                )}
                <div className="sksd-log-pagination-controls">
                    <div className="sksd-log-pagination-buttons">
                        <NfsButton
                            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage <= 1}
                            small
                            outlined
                            className="sksd-log-pagination-button"
                        >
                            Forrige
                        </NfsButton>
                        <span>
                            Side {currentPage} av {Math.max(1, Math.ceil(totalCount / pageSize) || 1)}
                        </span>
                        <NfsButton
                            onClick={() =>
                                setCurrentPage((p) =>
                                    Math.min(Math.max(1, Math.ceil(totalCount / pageSize) || 1), p + 1)
                                )
                            }
                            disabled={currentPage >= Math.max(1, Math.ceil(totalCount / pageSize) || 1)}
                            small
                            outlined
                            className="sksd-log-pagination-button"
                        >
                            Neste
                        </NfsButton>
                    </div>
                    <div className="sksd-log-pagination-info">
                        <span>
                            Viser {totalCount === 0 ? 0 : (currentPage - 1) * pageSize + 1}–
                            {Math.min(totalCount, currentPage * pageSize)} av {totalCount}
                        </span>
                        <label>
                            Per side:{" "}
                            <select
                                value={pageSize}
                                onChange={(e) => {
                                    const newSize = Number(e.target.value) || 20;
                                    setCurrentPage(1); // reset to first page when page size changes
                                    setPageSize(newSize);
                                }}
                            >
                                {[10, 20, 50, 100].map((size) => (
                                    <option key={size} value={size}>
                                        {size}
                                    </option>
                                ))}
                            </select>
                        </label>
                    </div>
                </div>
            </div>
            <div className="sksd-log-entries">
                {logData.map((log) => (
                    <SksdLogItem
                        key={log.attributes.objectid}
                        log={log}
                        isSksdAdmin={isSksdAdmin}
                        onShowXml={showSksdXml}
                        onReload={fetchLogData}
                    />
                ))}
            </div>
            <Modal
                isOpen={isModalOpen}
                onRequestClose={() => setIsModalOpen(false)}
                contentLabel="SKSD XML Melding"
                style={{
                    overlay: {
                        background: "rgba(37, 37, 37, 0.50)",
                    },
                    content: {
                        background: "var(--surface-surface-primary, #FFF)",
                    },
                }}
            >
                <div className="sksd-xml-content-container">
                    <div className="sksd-xml-content-container-header">
                        <span>{xmlShowHeader}</span>
                        <NfsButton
                            onClick={() => setIsModalOpen(false)}
                            outlined
                            className="sksd-xml-content-container-close-button"
                        >
                            Lukk
                        </NfsButton>
                    </div>

                    <textarea className="sksd-xml-content" value={xmlContent || ""} readOnly />
                </div>
            </Modal>
        </div>
    );
}
